import React, { useEffect, useState } from "react";
import { friendlyError } from "../../../middleware/common-functions";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Link, useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import {
  GET_SUPPLIER,
  GET_INVENTORY_INGREDIENTS,
  CREATE_SUPPLIER,
  UPDATE_SUPPLIER,
  DELETE_SUPPLIER,
  ADD_SUPPLIER_INGREDIENT,
  UPDATE_SUPPLIER_INGREDIENT,
  DELETE_SUPPLIER_INGREDIENT,
} from "./queries";
import {
  UNIT_OPTIONS,
  SupplierIngredient,
  InventoryIngredient,
} from "./models";
import { DeleteModal } from "../../modals/deletes";

type Params = { id?: string };

const SupplierForm: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const isEdit = !!id;

  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Add ingredient link state
  const [linkIngredientId, setLinkIngredientId] = useState("");
  const [linkPrice, setLinkPrice] = useState("");
  const [linkUnit, setLinkUnit] = useState("pz");
  const [linkMinQty, setLinkMinQty] = useState("");
  const [linkLeadDays, setLinkLeadDays] = useState("");
  const [linkSku, setLinkSku] = useState("");
  const [linkPreferred, setLinkPreferred] = useState(false);
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [showIngredientDropdown, setShowIngredientDropdown] = useState(false);

  const [fetchSupplier, { data, loading }] = useLazyQuery(GET_SUPPLIER, {
    fetchPolicy: "no-cache",
  });
  const [fetchIngredients, { data: ingredientsData }] = useLazyQuery(
    GET_INVENTORY_INGREDIENTS,
    { fetchPolicy: "no-cache" }
  );

  const [createSupplier] = useMutation(CREATE_SUPPLIER);
  const [updateSupplier] = useMutation(UPDATE_SUPPLIER);
  const [deleteSupplier] = useMutation(DELETE_SUPPLIER);
  const [addSupplierIngredient] = useMutation(ADD_SUPPLIER_INGREDIENT);
  const [updateSupplierIngredient] = useMutation(UPDATE_SUPPLIER_INGREDIENT);
  const [deleteSupplierIngredient] = useMutation(DELETE_SUPPLIER_INGREDIENT);

  useEffect(() => {
    fetchIngredients({ variables: { take: 500 } });
    if (isEdit) {
      fetchSupplier({ variables: { id } });
    }
    //eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (data?.getSupplier) {
      const s = data.getSupplier;
      setName(s.name);
      setContactName(s.contact_name || "");
      setPhone(s.phone || "");
      setEmail(s.email || "");
      setAddress(s.address || "");
      setNotes(s.notes || "");
    }
  }, [data]);

  const ingredients: InventoryIngredient[] =
    ingredientsData?.getInventoryIngredients || [];

  const filteredIngredients = ingredientSearch
    ? ingredients.filter((i) =>
        i.name.toLowerCase().includes(ingredientSearch.toLowerCase())
      )
    : ingredients;

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(lang("El nombre es obligatorio", "Name is required"));
      return;
    }
    try {
      const variables: any = {
        name,
        contact_name: contactName || null,
        phone: phone || null,
        email: email || null,
        address: address || null,
        notes: notes || null,
      };
      if (isEdit) {
        variables.id = id;
        await updateSupplier({ variables });
        toast.success(lang("Proveedor actualizado", "Supplier updated"));
        fetchSupplier({ variables: { id } });
      } else {
        const result = await createSupplier({ variables });
        toast.success(lang("Proveedor creado", "Supplier created"));
        history.push(
          `/inventory/suppliers/edit/${result.data.createSupplier.id}`
        );
      }
    } catch (err: any) {
      toast.error(friendlyError(err));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSupplier({ variables: { id } });
      toast.success(lang("Proveedor eliminado", "Supplier deleted"));
      history.push("/inventory/suppliers");
    } catch (err: any) {
      toast.error(friendlyError(err));
    }
  };

  const handleAddLink = async () => {
    if (!linkIngredientId || !linkPrice) {
      toast.error(
        lang(
          "Selecciona ingrediente y precio",
          "Select ingredient and price"
        )
      );
      return;
    }
    try {
      await addSupplierIngredient({
        variables: {
          supplier_id: parseInt(id!),
          ingredient_id: parseInt(linkIngredientId),
          unit_price: parseFloat(linkPrice),
          unit: linkUnit,
          min_order_qty: linkMinQty ? parseFloat(linkMinQty) : null,
          lead_time_days: linkLeadDays ? parseInt(linkLeadDays) : null,
          sku: linkSku || null,
          is_preferred: linkPreferred,
        },
      });
      toast.success(lang("Ingrediente vinculado", "Ingredient linked"));
      setLinkIngredientId("");
      setLinkPrice("");
      setLinkSku("");
      setLinkMinQty("");
      setLinkLeadDays("");
      setLinkPreferred(false);
      setIngredientSearch("");
      fetchSupplier({ variables: { id } });
    } catch (err: any) {
      toast.error(friendlyError(err));
    }
  };

  const handleTogglePreferred = async (si: SupplierIngredient) => {
    try {
      await updateSupplierIngredient({
        variables: { id: si.id, is_preferred: !si.is_preferred },
      });
      fetchSupplier({ variables: { id } });
    } catch (err: any) {
      toast.error(friendlyError(err));
    }
  };

  const handleRemoveLink = async (siId: string) => {
    try {
      await deleteSupplierIngredient({ variables: { id: siId } });
      fetchSupplier({ variables: { id } });
    } catch (err: any) {
      toast.error(friendlyError(err));
    }
  };

  if (loading) return <LoaderDots fullSize />;

  const supplierIngredients: SupplierIngredient[] =
    data?.getSupplier?.ingredients || [];

  return (
    <>
      <p>
        <Link to="/inventory/suppliers">
          <i className="fa fa-chevron-left" aria-hidden="true"></i>{" "}
          {lang("Proveedores", "Suppliers")}
        </Link>
      </p>
      <h1>
        {isEdit
          ? lang("Editar Proveedor", "Edit Supplier")
          : lang("Nuevo Proveedor", "New Supplier")}
      </h1>

      <div className="row">
        <div className="col-lg-5">
          <div className="material_card p-3 mb-3">
            <h4>{lang("Datos del proveedor", "Supplier details")}</h4>
            <div className="form-group">
              <label>{lang("Nombre", "Name")} *</label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{lang("Contacto", "Contact")}</label>
              <input
                type="text"
                className="form-control"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </div>
            <div className="row">
              <div className="col-6">
                <div className="form-group">
                  <label>{lang("Teléfono", "Phone")}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>{lang("Dirección", "Address")}</label>
              <input
                type="text"
                className="form-control"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{lang("Notas", "Notes")}</label>
              <textarea
                className="form-control"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </div>

            <div className="d-flex justify-content-between mt-3">
              {isEdit && (
                <button
                  className="btn btn-outline-danger"
                  onClick={() => setShowDelete(true)}
                >
                  <i className="fa fa-trash"></i> {lang("Eliminar", "Delete")}
                </button>
              )}
              <button className="btn btn-primary" onClick={handleSave}>
                <i className="far fa-save"></i> {lang("Guardar", "Save")}
              </button>
            </div>
          </div>
        </div>

        {/* Linked ingredients (edit only) */}
        {isEdit && (
          <div className="col-lg-7">
            <div className="material_card p-3 mb-3">
              <h4>
                {lang("Ingredientes que provee", "Ingredients supplied")}
              </h4>

              {supplierIngredients.length > 0 && (
                <div className="table-responsive mb-3">
                  <table className="table table-sm">
                    <thead className="thead-light">
                      <tr>
                        <th>{lang("Ingrediente", "Ingredient")}</th>
                        <th>{lang("Precio/U", "Price/U")}</th>
                        <th>{lang("Unidad", "Unit")}</th>
                        <th>{lang("Min", "Min")}</th>
                        <th>{lang("Días", "Days")}</th>
                        <th>{lang("Pref.", "Pref.")}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplierIngredients.map((si) => (
                        <tr key={si.id}>
                          <td>
                            {si.ingredient?.name || `#${si.ingredient_id}`}
                          </td>
                          <td>${Number(si.unit_price).toFixed(2)}</td>
                          <td>{si.unit}</td>
                          <td>{si.min_order_qty || "—"}</td>
                          <td>{si.lead_time_days || "—"}</td>
                          <td>
                            <button
                              className={`btn btn-sm btn-${
                                si.is_preferred ? "warning" : "outline-secondary"
                              }`}
                              onClick={() => handleTogglePreferred(si)}
                              title={lang(
                                "Marcar como preferido",
                                "Mark as preferred"
                              )}
                            >
                              <i className="fas fa-star"></i>
                            </button>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveLink(si.id)}
                            >
                              <i className="fa fa-times"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <hr />
              <h5>{lang("Vincular ingrediente", "Link ingredient")}</h5>
              <div className="row">
                <div className="col-6 position-relative">
                  <div className="form-group">
                    <label>{lang("Ingrediente", "Ingredient")}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={lang("Buscar...", "Search...")}
                      value={ingredientSearch}
                      onChange={(e) => {
                        setIngredientSearch(e.target.value);
                        setShowIngredientDropdown(true);
                        setLinkIngredientId("");
                      }}
                      onFocus={() => setShowIngredientDropdown(true)}
                      onBlur={() =>
                        setTimeout(() => setShowIngredientDropdown(false), 200)
                      }
                    />
                    {showIngredientDropdown && !linkIngredientId && (
                      <div
                        className="list-group position-absolute w-100"
                        style={{
                          zIndex: 10,
                          maxHeight: 150,
                          overflowY: "auto",
                        }}
                      >
                        {filteredIngredients.slice(0, 15).map((ing) => (
                          <button
                            key={ing.id}
                            type="button"
                            className="list-group-item list-group-item-action py-1"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setLinkIngredientId(ing.id);
                              setIngredientSearch(ing.name);
                              setLinkUnit(ing.unit);
                              setShowIngredientDropdown(false);
                            }}
                          >
                            <small>
                              {ing.name}{" "}
                              <span className="text-muted">({ing.unit})</span>
                            </small>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-3">
                  <div className="form-group">
                    <label>{lang("Precio/U", "Price/U")}</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={linkPrice}
                      onChange={(e) => setLinkPrice(e.target.value)}
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
                <div className="col-3">
                  <div className="form-group">
                    <label>{lang("Unidad", "Unit")}</label>
                    <select
                      className="custom-select custom-select-sm"
                      value={linkUnit}
                      onChange={(e) => setLinkUnit(e.target.value)}
                    >
                      {UNIT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.value}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-3">
                  <div className="form-group">
                    <label>{lang("Pedido mín.", "Min order")}</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={linkMinQty}
                      onChange={(e) => setLinkMinQty(e.target.value)}
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
                <div className="col-3">
                  <div className="form-group">
                    <label>{lang("Días entrega", "Lead days")}</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={linkLeadDays}
                      onChange={(e) => setLinkLeadDays(e.target.value)}
                      min="0"
                    />
                  </div>
                </div>
                <div className="col-3">
                  <div className="form-group">
                    <label>SKU</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={linkSku}
                      onChange={(e) => setLinkSku(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-3 d-flex align-items-end">
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={linkPreferred}
                        onChange={(e) => setLinkPreferred(e.target.checked)}
                        id="linkPreferred"
                      />
                      <label
                        className="form-check-label"
                        htmlFor="linkPreferred"
                      >
                        <small>{lang("Preferido", "Preferred")}</small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={handleAddLink}
              >
                <i className="fa fa-plus"></i>{" "}
                {lang("Vincular", "Link")}
              </button>
            </div>
          </div>
        )}
      </div>

      <DeleteModal
        open={showDelete}
        title={lang(
          `¿Eliminar proveedor "${name}"?`,
          `Delete supplier "${name}"?`
        )}
        toggle={() => setShowDelete(false)}
        handleConfirm={handleDelete}
        confirm_txt={lang("Eliminar", "Delete")}
      >
        <p>
          {lang(
            "El proveedor se eliminará permanentemente",
            "The supplier will be permanently deleted"
          )}
        </p>
      </DeleteModal>
    </>
  );
};

export default SupplierForm;
